import { inject as service } from '@ember/service';
import Controller, { inject as controller } from '@ember/controller';
import { dropTask } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import ignorePromiseFailure from 'cio/utils/ignore-promise-failure';
import AlertsService from 'cio/services/alerts';
import ApiService from 'cio/services/api';
import ApiErrorsService from 'cio/services/api-errors';
import AuthService from 'cio/services/auth';
import RouterService from '@ember/routing/router-service';
import ObjectModel from 'cio/models/object';
import ObjectTypeModel from 'cio/models/object-type';
import { action } from '@ember/object';
import DS from 'ember-data';
import IndexController from 'cio/pods/object/index/controller';
import ObjectRelationshipsController from 'cio/pods/object/relationships/controller';
import ObjectTypesService from 'cio/services/object-types';
import { ObjectIdentifierKey } from 'cio/utils/identifiers';

export default class ObjectController extends Controller {
  @service alerts!: AlertsService;
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service auth!: AuthService;
  @service router!: RouterService;
  @service store!: DS.Store;
  @service('objectTypes') objectTypesService!: ObjectTypesService;

  @tracked showDeleteModal = false;
  @tracked showRelationshipsModal = false;
  @tracked showExportModal = false;
  @tracked isShowingExportProgress = false;
  @controller('object.index') objectIndexController!: IndexController;
  @controller('object.relationships') objectRelationshipsController!: ObjectRelationshipsController;

  exportPendingImage = new URL('cio/assets/images/export-pending.svg', import.meta.url).toString();
  exportSuccessImage = new URL('cio/assets/images/export-success.svg', import.meta.url).toString();
  exportFailureImage = new URL('cio/assets/images/export-failure.svg', import.meta.url).toString();

  declare model: ObjectModel;

  get optionsDropdownOptions(): {
    label: string;
    action(): void;
  }[] {
    let options = [];

    options.push(
      {
        label: 'Delete',
        action: () => (this.showDeleteModal = true),
        disabled: !this.objectTypesService.canConfigureObjects,
        tooltip: {
          condition: !this.objectTypesService.canConfigureObjects,
          content: 'Contact an Admin to access this feature.',
        },
      },
      {
        label: `Export profile data`,
        action: () => (this.showExportModal = true),
        disabled: !this.auth.hasPermission('journeys/exports/create'),
        tooltip: {
          condition: !this.auth.hasPermission('journeys/exports/create'),
          content: 'Contact an Admin to access this feature.',
        },
      },
      {
        label: 'Add Relationships',
        action: () => (this.showRelationshipsModal = true),
        disabled: !this.objectTypesService.canCreateRelationships,
        tooltip: {
          condition: !this.objectTypesService.canCreateRelationships,
          content: 'Contact an Admin to access this feature.',
        },
      },
    );

    return options.map((o) => ({ ...o, classNames: 'whitespace-nowrap' }));
  }

  get objectType() {
    return this.model.belongsTo('objectType').value() as ObjectTypeModel; // cast type as it guaranteed to be defined in if user has got so far
  }

  @action
  onRelationshipsAdd() {
    this.showRelationshipsModal = false;

    if (this.router.currentRouteName === 'object.relationships') {
      this.objectRelationshipsController.reloadRelationshipsTable();
    } else if (this.router.currentRouteName === 'object.index') {
      this.objectIndexController.reloadRelationshipsTable();
    }
  }

  @action
  showExportProgressModal() {
    this.isShowingExportProgress = true;
    this.showExportModal = false;
  }

  @action
  exportObject() {
    let data = { internal_id: this.model.identifiers[ObjectIdentifierKey.CioObjectId] };
    return this.api.post('exports/object_data', { data });
  }

  private deleteObject() {
    let { objectType } = this;

    let promise = this.model.destroyRecord();

    this.apiErrors.attach(promise);

    promise.then(() => {
      this.router.transitionTo('objects.type', objectType.slug);
    }, ignorePromiseFailure);
    this.store.findAll('object-type');

    return promise;
  }

  deleteTask = dropTask(async () => {
    try {
      await this.deleteObject();
    } catch (e) {
      this.apiErrors.report(e);
      throw e;
    }

    this.showDeleteModal = false;
  });
}

// DO NOT DELETE: this is how TypeScript knows how to look up your controllers.
declare module '@ember/controller' {
  interface Registry {
    object: ObjectController;
  }
}
